import { get, post, put, del } from '@/lib/api/client'
import {
  ProgrammeFilterParamsSchema,
  PaginationParamsSchema,
  CreateProgrammeSchema,
  UpdateProgrammeSchema,
  ProgrammeStatsResponseSchema,
  type Programme,
  type ProgrammeDetail,
  type ProgrammeFilterParams,
  type CreateProgramme,
  type UpdateProgramme,
  type ProgrammeStatsResponse,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams
} from '@/lib/api/types'

export class ProgrammeService {

  async getProgrammes(
    params: PaginationParams = { page: 1, size: 10 },
    filters: ProgrammeFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Programme>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = ProgrammeFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<Programme>>>(
      `programmes/private/filter?${query}`,
      validatedFilters
    )
  }


  async createProgramme(data: CreateProgramme): Promise<ApiResponse<ProgrammeDetail>> {

    const validatedData = CreateProgrammeSchema.parse(data)


    const payload = {
      title: validatedData.title,
      coverImageTitle: validatedData.coverImageTitle,
      articleTag: validatedData.articleTag, // Array of strings
      ...(validatedData.landingPageAfsaProgramme !== undefined && { landingPageAfsaProgramme: validatedData.landingPageAfsaProgramme }), // Now managed via WebPages
      allowComments: validatedData.allowComments,
      content: validatedData.content,
      status: validatedData.status,
      coverImageUrl: validatedData.coverImageUrl, // Image URL string - API expects this field name
      websiteType: validatedData.websiteType,
      websiteLink: validatedData.websiteLink,
      isAddWebsite: validatedData.isAddWebsite,
      microSiteRequest: validatedData.microSiteRequest
    }

    return await post<ApiResponse<ProgrammeDetail>>(
      'programmes/admin/private',
      payload
    )
  }


  async updateProgramme(
    programmeId: string,
    data: UpdateProgramme
  ): Promise<ApiResponse<null>> {
    // Validate data
    const validatedData = UpdateProgrammeSchema.parse(data)

    // Use JSON body instead of FormData since we're sending URLs
    const payload = {
      title: validatedData.title,
      coverImageTitle: validatedData.coverImageTitle,
      articleTag: validatedData.articleTag, // Array of strings
      ...(validatedData.landingPageAfsaProgramme !== undefined && { landingPageAfsaProgramme: validatedData.landingPageAfsaProgramme }), // Now managed via WebPages
      allowComments: validatedData.allowComments,
      content: validatedData.content,
      ...(validatedData.status && { status: validatedData.status }),
      ...(validatedData.coverImageUrl && { coverImageUrl: validatedData.coverImageUrl }),
      websiteType: validatedData.websiteType,
      websiteLink: validatedData.websiteLink,
      isAddWebsite: validatedData.isAddWebsite,
      microSiteRequest: validatedData.microSiteRequest
    }

    return await put<ApiResponse<null>>(
      `programmes/admin/private/${programmeId}`,
      payload
    )
  }


  async updateProgrammeStatus(programmeId: string, status: 'DRAFT' | 'PUBLISH'): Promise<ApiResponse<Programme>> {
    return await put<ApiResponse<Programme>>(
      `programmes/admin/private/${programmeId}/status`,
      { status }
    )
  }


  async deleteProgramme(programmeId: string): Promise<ApiResponse<null>> {
    return await del<ApiResponse<null>>(
      `programmes/admin/private/${programmeId}`
    )
  }


  async getProgrammeById(programmeId: string): Promise<ApiResponse<ProgrammeDetail>> {
    return await get<ApiResponse<ProgrammeDetail>>(
      `programmes/private/${programmeId}`
    )
  }


  async getTotalPublishedProgrammes(): Promise<ApiResponse<{ publishedProgrammes: number }>> {
    return await get<ApiResponse<{ publishedProgrammes: number }>>(
      'programmes/private/count/published'
    )
  }


  async getTotalDraftProgrammes(): Promise<ApiResponse<{ draftProgrammes: number }>> {
    return await get<ApiResponse<{ draftProgrammes: number }>>(
      'programmes/private/count/draft'
    )
  }

  async getProgrammeStats(): Promise<ProgrammeStatsResponse> {
    const res = await get('programmes/private/stats')
    return ProgrammeStatsResponseSchema.parse(res)
  }

  async searchProgrammes(
    params: PaginationParams = { page: 1, size: 10 },
    searchBy?: string
  ): Promise<ApiResponse<PaginatedResponse<Programme>>> {
    const validatedParams = PaginationParamsSchema.parse(params)

    const queryParams = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    })

    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<PaginatedResponse<Programme>>>(
      `programmes/private/search?${queryParams.toString()}`
    )
  }

  /**
   * Get programme highlights
   * GET /programmes/public/highlights?type={type}
   */
  async getProgrammeHighlights(type: string = 'HOME_PAGE_AFSA_PROGRAMME'): Promise<ApiResponse<Programme[]>> {
    return await get<ApiResponse<Programme[]>>(
      `programmes/public/highlights?type=${type}`
    )
  }

  /**
   * Update programme highlights
   * POST /programmes/private/highlight
   */
  async updateProgrammeHighlights(
    programmeIds: string[],
    type: string = 'HOME_PAGE_AFSA_PROGRAMME'
  ): Promise<ApiResponse<null>> {
    return await post<ApiResponse<null>>('programmes/private/highlight', {
      programmeIds,
      programmeHighlightType: type
    })
  }
}


export const programmeService = new ProgrammeService()

// Export default
export default programmeService
